import React, { useState, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";

const Menu = () => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const { user, wallet, logout, isAdmin } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getKycBadge = () => {
    if (!user) return null;
    switch (user.kycStatus) {
      case "verified":
        return <span style={{ backgroundColor: "#e8f5e9", color: "#2e7d32", padding: "2px 8px", borderRadius: "10px", fontSize: "11px", fontWeight: "bold" }}>KYC Verified</span>;
      case "pending":
        return <span style={{ backgroundColor: "#fff8e1", color: "#f57f17", padding: "2px 8px", borderRadius: "10px", fontSize: "11px", fontWeight: "bold" }}>KYC Pending</span>;
      case "rejected":
        return <span style={{ backgroundColor: "#ffebee", color: "#c62828", padding: "2px 8px", borderRadius: "10px", fontSize: "11px", fontWeight: "bold" }}>KYC Rejected</span>;
      default:
        return <span style={{ backgroundColor: "#eceff1", color: "#546e7a", padding: "2px 8px", borderRadius: "10px", fontSize: "11px" }}>KYC Not Submitted</span>;
    }
  };

  const currentPath = location.pathname;

  const navItems = [
    { label: "Dashboard", path: "/" },
    { label: "Orders", path: "/orders" },
    { label: "Holdings", path: "/holdings" },
    { label: "Positions", path: "/positions" },
    { label: "Option Chain", path: "/option-chain", badge: "⚡" },
    { label: "Strategy Builder", path: "/strategy-builder", badge: "📐" },
    { label: "Funds", path: "/funds", amount: wallet ? `(₹${wallet.marginAvailable?.toLocaleString('en-IN')})` : null },
    { label: "Support", path: "/tickets" },
  ];

  const userIdDisplay = user?.name ? user.name.split(" ")[0].toUpperCase() : "CC0344";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "24px", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
      
      {/* Zerodha Red Kite Logo Mark */}
      <Link to="/" style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
        <div style={{
          width: "28px",
          height: "28px",
          backgroundColor: "#ff5722",
          clipPath: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginRight: "10px"
        }} />
      </Link>

      {/* Layout Selector: Default [NEW] */}
      <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", border: "1px solid #e0e0e0", padding: "3px 10px", borderRadius: "4px", backgroundColor: "#fafafa" }}>
        <span style={{ color: "#666" }}>Default</span>
        <span style={{ backgroundColor: "#ffebee", color: "#ef5350", fontSize: "9px", padding: "1px 5px", borderRadius: "3px", fontWeight: "bold" }}>NEW</span>
        <span style={{ fontSize: "10px", color: "#999" }}>▼</span>
      </div>

      {/* Main Nav Items */}
      <nav style={{ display: "flex", alignItems: "center", gap: "22px" }}>
        {navItems.map((item) => {
          const isActive = currentPath === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              style={{
                textDecoration: "none",
                fontSize: "13px",
                fontWeight: isActive ? 600 : 400,
                color: isActive ? "#ff5722" : "#444",
                padding: "14px 0",
                borderBottom: isActive ? "2px solid #ff5722" : "2px solid transparent",
                transition: "all 0.15s ease-in-out",
                display: "flex",
                alignItems: "center",
                gap: "4px"
              }}
            >
              {item.badge && <span style={{ fontSize: "12px" }}>{item.badge}</span>}
              <span>{item.label}</span>
              {item.amount && <span style={{ color: "#4caf50", fontWeight: 500, marginLeft: "4px", fontSize: "12px" }}>{item.amount}</span>}
            </Link>
          );
        })}

        {isAdmin && (
          <Link
            to="/admin"
            style={{
              textDecoration: "none",
              fontSize: "12px",
              fontWeight: "bold",
              color: "#ef5350",
              backgroundColor: "#ffebee",
              padding: "4px 10px",
              borderRadius: "4px"
            }}
          >
            👑 Admin
          </Link>
        )}
      </nav>

      {/* Right Icons: Cart, Notifications, Profile Avatar */}
      <div style={{ display: "flex", alignItems: "center", gap: "16px", marginLeft: "10px" }}>
        <span title="Basket Orders" style={{ cursor: "pointer", fontSize: "15px", color: "#666" }}>🛒</span>
        <span title="Notifications" style={{ cursor: "pointer", fontSize: "15px", color: "#666" }}>🔔</span>

        {/* User Avatar Circle */}
        <div
          onClick={() => setIsProfileOpen(!isProfileOpen)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
            position: "relative"
          }}
        >
          <div style={{
            width: "28px",
            height: "28px",
            borderRadius: "50%",
            backgroundColor: "#2b3b52",
            color: "#ffffff",
            fontSize: "11px",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}>
            {user?.name ? user.name.substring(0, 2).toUpperCase() : "OB"}
          </div>
          <span style={{ fontSize: "12px", color: "#666", fontWeight: 500 }}>{userIdDisplay}</span>
        </div>

        {/* Profile Dropdown Drawer */}
        {isProfileOpen && (
          <div
            style={{
              position: "absolute",
              right: "20px",
              top: "50px",
              backgroundColor: "#ffffff",
              boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
              borderRadius: "8px",
              width: "240px",
              zIndex: 1000,
              padding: "16px",
              border: "1px solid #e0e0e0",
            }}
          >
            <div style={{ paddingBottom: "12px", borderBottom: "1px solid #eee", marginBottom: "12px" }}>
              <div style={{ fontWeight: "bold", fontSize: "14px", color: "#333" }}>{user?.name}</div>
              <div style={{ fontSize: "12px", color: "#666" }}>{user?.email}</div>
              <div style={{ marginTop: "6px" }}>{getKycBadge()}</div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <Link to="/funds" onClick={() => setIsProfileOpen(false)} style={{ textDecoration: "none", color: "#333", fontSize: "13px" }}>
                💼 Virtual Wallet & Funds
              </Link>
              <Link to="/option-chain" onClick={() => setIsProfileOpen(false)} style={{ textDecoration: "none", color: "#333", fontSize: "13px" }}>
                ⚡ Option Chain & Greeks
              </Link>
              <Link to="/tickets" onClick={() => setIsProfileOpen(false)} style={{ textDecoration: "none", color: "#333", fontSize: "13px" }}>
                💬 Help & Support
              </Link>
              {isAdmin && (
                <Link to="/admin" onClick={() => setIsProfileOpen(false)} style={{ textDecoration: "none", color: "#ef5350", fontWeight: "bold", fontSize: "13px" }}>
                  👑 Admin Console
                </Link>
              )}
              <hr style={{ margin: "4px 0", border: "none", borderTop: "1px solid #eee" }} />
              <button
                onClick={handleLogout}
                style={{
                  backgroundColor: "#ffebee",
                  color: "#c62828",
                  border: "none",
                  padding: "8px 12px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: "bold",
                  fontSize: "13px",
                  textAlign: "left",
                  width: "100%",
                }}
              >
                🚪 Logout
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Menu;
